import type { CustomRouteItem } from './index';
import type { RouteTimelineDtoModelType, VisitDtoModelType } from '../../../../store/mst/models';

const getStopItemId = (item?: CustomRouteItem): string | undefined => {
  const id = item?.routeTimeline?.id ?? item?.id;
  return id === undefined || id === null ? undefined : String(id);
};

export const isDraggedStopSelected = (
  draggedVisit?: CustomRouteItem,
  selectedStops: RouteTimelineDtoModelType[] = [],
): boolean => {
  const draggedStopId = draggedVisit?.routeTimeline?.id;

  if (!draggedStopId) {
    return false;
  }

  return selectedStops.some((stop) => stop.id === draggedStopId);
};

// When the user drags a selected stop, every selected stop should look like part of the drag.
export const isDraggingSelectedStop = (
  item?: CustomRouteItem,
  draggingVisit?: CustomRouteItem,
  selectedStopIds?: ReadonlySet<string>,
): boolean => {
  if (!item || !draggingVisit) {
    return false;
  }

  const itemId = getStopItemId(item);
  const draggingVisitId = getStopItemId(draggingVisit);

  if (itemId && itemId === draggingVisitId) {
    return true;
  }

  // The dragged item is cloned; use the shared ID set instead of scanning the selection per item.
  const itemStopId = item.routeTimeline?.id;
  const draggedStopId = draggingVisit.routeTimeline?.id;
  return !!(itemStopId && draggedStopId && selectedStopIds?.has(draggedStopId) && selectedStopIds.has(itemStopId));
};

// Dragging an unselected stop moves only that stop; dragging a selected stop moves the selected set.
const getSelectedDragStops = (
  draggedVisit?: CustomRouteItem,
  selectedStops: RouteTimelineDtoModelType[] = [],
): RouteTimelineDtoModelType[] => {
  const draggedStop = draggedVisit?.routeTimeline;

  if (!draggedStop) {
    return [];
  }

  const draggedStopIsSelected = isDraggedStopSelected(draggedVisit, selectedStops);
  if (!draggedStopIsSelected || selectedStops.length <= 1) {
    return [draggedStop];
  }

  return selectedStops.some((stop) => stop.id === draggedStop.id)
    ? selectedStops
    : [draggedStop];
};

const getSelectedUnscheduledOrderUuids = (selectedUnscheduledOrders: VisitDtoModelType[] = []): string[] => (
  selectedUnscheduledOrders
    .map((order) => order.uuid)
    .filter((uuid): uuid is string => !!uuid)
);

const isStopAlreadyOnTargetRoute = (
  stop: RouteTimelineDtoModelType,
  targetRoute?: CustomRouteItem['route'],
): boolean => {
  if (!targetRoute) {
    return false;
  }

  return (!!stop.route?.id && stop.route.id === targetRoute.id)
    || (!!stop.route?.uuid && stop.route.uuid === targetRoute.uuid);
};

// A cross-route drop only needs order UUIDs for stops that are not already on the target route.
const getMovableScheduledOrderUuids = (
  selectedDragStops: RouteTimelineDtoModelType[],
  targetRoute?: CustomRouteItem['route'],
): string[] => (
  selectedDragStops
    .filter((stop) => !isStopAlreadyOnTargetRoute(stop, targetRoute))
    .flatMap((stop) => stop.orders ?? [])
    .map((order) => order.uuid)
    .filter((uuid): uuid is string => !!uuid)
);

// Selected unscheduled orders join the drag only when the dragged scheduled stop is part of the selection.
const getUnscheduledOrderUuidsForDrag = (
  draggedStopIsSelected: boolean,
  selectedUnscheduledOrders: VisitDtoModelType[],
): string[] => (
  draggedStopIsSelected
    ? getSelectedUnscheduledOrderUuids(selectedUnscheduledOrders)
    : []
);

const isMoveOnlyDragSelection = (
  draggedStopIsSelected: boolean,
  selectedDragStops: RouteTimelineDtoModelType[],
  unscheduledOrderUuids: string[],
): boolean => (
  draggedStopIsSelected
    && (selectedDragStops.length > 1 || unscheduledOrderUuids.length > 0)
);

export interface TimelineDragSelectionState {
  selectedStops: RouteTimelineDtoModelType[];
  orderUuids: string[];
  isMoveOnly: boolean;
}

// This describes what will move when the user drags a stop across routes.
export const getTimelineDragSelectionState = (
  draggedVisit?: CustomRouteItem,
  selectedStops: RouteTimelineDtoModelType[] = [],
  selectedUnscheduledOrders: VisitDtoModelType[] = [],
  targetRoute?: CustomRouteItem['route'],
): TimelineDragSelectionState => {
  const draggedStopIsSelected = isDraggedStopSelected(draggedVisit, selectedStops);
  const selectedDragStops = getSelectedDragStops(draggedVisit, selectedStops);
  const stopOrderUuids = getMovableScheduledOrderUuids(selectedDragStops, targetRoute);
  const unscheduledOrderUuids = getUnscheduledOrderUuidsForDrag(draggedStopIsSelected, selectedUnscheduledOrders);

  return {
    selectedStops: selectedDragStops,
    orderUuids: [...new Set([...stopOrderUuids, ...unscheduledOrderUuids])],
    isMoveOnly: isMoveOnlyDragSelection(draggedStopIsSelected, selectedDragStops, unscheduledOrderUuids),
  };
};
